import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import { isImageUnavailable } from './explanation';
import type { Decision } from './types';

type BadgeColor = 'success' | 'warning' | 'error' | 'default';

// Legacy decisions are shown with the same friendly label as their modern
// equivalent — the "(legacy)" distinction belongs in Technical details, not
// the primary badge. "Image unavailable" is deliberately distinct from
// "Inconclusive": one means no comparison ran, the other means it ran and
// didn't produce a strong signal either way.
function friendlyDecision(decision: Decision, imageStatus?: string | null): { label: string; color: BadgeColor } {
  if (decision === 'UNKNOWN' && imageStatus !== undefined && isImageUnavailable(imageStatus ?? null)) {
    return { label: 'Image unavailable', color: 'default' };
  }
  switch (decision) {
    case 'STRONG_MATCH':
    case 'LEGACY_STRONG_SIGNAL':
      return { label: 'Strong match', color: 'success' };
    case 'STRONG_MISMATCH':
      return { label: 'Strong mismatch', color: 'error' };
    case 'INCONCLUSIVE':
    case 'LEGACY_NO_STRONG_SIGNAL':
      return { label: 'Inconclusive', color: 'warning' };
    case 'UNKNOWN':
    default:
      return { label: 'Unscored', color: 'default' };
  }
}

export const DecisionBadge = ({
  decision,
  imageStatus,
}: {
  decision: Decision;
  imageStatus?: string | null;
}) => {
  const theme = useTheme();
  const meta = friendlyDecision(decision, imageStatus);

  const palette =
    meta.color === 'default'
      ? { bg: theme.palette.action.selected, fg: theme.palette.text.secondary }
      : {
          bg: alpha(theme.palette[meta.color].main, 0.14),
          fg: theme.palette[meta.color].main,
        };

  return (
    <Chip
      label={meta.label}
      size="small"
      sx={{
        backgroundColor: palette.bg,
        color: palette.fg,
        fontSize: 12,
        fontWeight: 700,
        height: 24,
      }}
    />
  );
};
